// Central content source for the Amden Launch page.
// Structured so a future website admin area can manage every field
// without touching page components.

export const LAUNCH_SEO = {
  title: 'Start a Business with Practical Launch Support | Amden Group',
  description:
    'Turn your business idea into a structured, market-ready operation with planning, branding, website, systems and hands-on launch support from Amden Group.',
};

const ENQUIRY_BASE = '/?service=launch&source=Launch%20Page';

export const LAUNCH_HERO = {
  eyebrow: 'Amden Launch',
  headingLines: ['Start right.', 'Build smart.', 'Launch successfully.'],
  accentLineIndex: 2,
  body: 'From first idea to a fully functioning business, Amden provides the practical guidance, brand, website, systems and operational support needed to launch with stronger foundations.',
  credibility: 'Practical support from concept through launch and early growth.',
  primaryCta: {
    label: 'Start Your Business Journey',
    href: `${ENQUIRY_BASE}#solution-finder`,
  },
  secondaryCta: {
    label: 'Explore Launch Support',
    target: '#launch-services',
  },
  image: {
    src: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80',
    alt: 'Founder and small team actively planning and building a new business together',
    width: 1200,
    height: 900,
  },
  floatingCard: {
    icon: 'rocket',
    label: 'Your journey',
    text: 'Your business journey starts here.',
  },
};

export const LAUNCH_STARTING_POINTS = {
  eyebrow: 'Your Journey',
  heading: 'Where are you starting from?',
  intro: 'Every business journey is different. Choose the option that best describes where you are now.',
  options: [
    {
      id: 'idea',
      icon: 'lightbulb',
      title: 'I Have an Idea',
      description: 'I have an early concept and need help testing whether it can become a viable business.',
      recommendation:
        'Start with a Business Idea Review. We’ll examine the concept, the intended customer and the commercial assumptions, then agree the practical next steps before you invest further.',
      relevantServices: ['business-idea-review', 'business-planning'],
    },
    {
      id: 'ready',
      icon: 'flag',
      title: 'I’m Ready to Start',
      description: 'I understand what I want to build and need a practical plan, structure and launch support.',
      recommendation:
        'You’re ready for structure. A Launch Strategy sets the offer, audience and route to market — and the Full Business Launch Programme can carry it through to a professional launch.',
      relevantServices: ['launch-strategy', 'full-launch-programme'],
    },
    {
      id: 'rebuild',
      icon: 'wrench',
      title: 'I Need to Rebuild the Foundations',
      description: 'I have started trading, but the business needs clearer structure, systems, branding or direction.',
      recommendation:
        'Let’s strengthen what’s already there. The Systems Starter Package and Business Website Package rebuild the practical foundations without starting over.',
      relevantServices: ['systems-starter', 'business-website'],
    },
    {
      id: 'new-launch',
      icon: 'rocket',
      title: 'I’m Launching Something New',
      description: 'I’m an established business launching a new product, service, division or market proposition.',
      recommendation:
        'Treat it like a focused launch project. A Launch Strategy defines the proposition and route to market, supported by the Brand Starter Package where a distinct identity is needed.',
      relevantServices: ['launch-strategy', 'brand-starter'],
    },
  ],
};

export const LAUNCH_WHAT_WE_BUILD = {
  eyebrow: 'What We Help You Build',
  heading: 'Everything needed to turn an idea into a working business',
  intro:
    'Amden brings the key parts of business development together, helping you avoid disconnected suppliers, unclear priorities and unnecessary delays.',
  blocks: [
    {
      id: 'direction',
      icon: 'compass',
      title: 'Business Direction',
      items: ['Idea and viability review', 'Business model', 'Target customer', 'Market positioning', 'Commercial priorities'],
    },
    {
      id: 'planning',
      icon: 'clipboard-list',
      title: 'Business Planning',
      items: ['Practical business plan', 'Launch roadmap', 'Financial assumptions', 'Pricing considerations', 'Goals and measures'],
    },
    {
      id: 'brand',
      icon: 'palette',
      title: 'Brand and Market Presence',
      items: ['Business naming support', 'Brand positioning', 'Visual identity', 'Messaging', 'Marketing launch plan'],
    },
    {
      id: 'website',
      icon: 'monitor',
      title: 'Website and Digital Presence',
      items: ['Website strategy and development', 'Domain and email setup support', 'Enquiry and booking journeys', 'Analytics foundations', 'Digital launch requirements'],
    },
    {
      id: 'operations',
      icon: 'cog',
      title: 'Operations and Systems',
      items: ['Workflow design', 'CRM and customer tracking', 'Reporting', 'Documentation', 'Scalable operating processes'],
    },
    {
      id: 'people',
      icon: 'users',
      title: 'People and Compliance Foundations',
      items: ['Initial role structure', 'Recruitment support', 'Essential HR documentation', 'Training requirements', 'Referral to appropriate regulated specialists where needed'],
    },
  ],
  disclaimer:
    'Legal, tax, accounting, company-secretarial and regulated financial advice must be provided by appropriately qualified professionals. Amden can help coordinate these requirements as part of your launch, but does not provide regulated advice.',
};

export const LAUNCH_PROBLEMS = {
  eyebrow: 'Problems We Solve',
  heading: 'Problems We Solve',
  intro:
    'Starting a business can become overwhelming when every decision depends on another. Amden helps turn uncertainty into a structured and manageable launch plan.',
  items: [
    {
      id: 'idea-to-reality',
      icon: 'lightbulb',
      title: 'Turning an Idea into Reality',
      description: 'You have a promising concept but are unsure how to test, structure and progress it.',
      howWeHelp:
        'We review the idea against the intended customer, market and commercial assumptions — then turn it into a structured, testable plan.',
    },
    {
      id: 'planning-gaps',
      icon: 'list-checks',
      title: 'Planning Gaps',
      description: 'You need a practical business plan and roadmap rather than a document that sits unused.',
      howWeHelp:
        'We build a working plan — priorities, costs, actions and measures — that guides decisions week by week, not a document that gathers dust.',
    },
    {
      id: 'unclear-position',
      icon: 'crosshair',
      title: 'Unclear Market Position',
      description: 'The offer, audience or reason customers should choose the business is not yet clear.',
      howWeHelp:
        'We define the offer, the audience and the reason to choose you — then carry that message through your brand, website and launch activity.',
    },
    {
      id: 'disconnected-suppliers',
      icon: 'unplug',
      title: 'Too Many Disconnected Suppliers',
      description: 'Branding, websites, systems and advice are being handled separately without one joined-up plan.',
      howWeHelp:
        'Amden coordinates strategy, brand, website and systems under one roadmap, so every part supports the same commercial goal.',
    },
    {
      id: 'limited-time',
      icon: 'clock',
      title: 'Limited Time or Experience',
      description: 'You need hands-on support to keep decisions and deliverables moving.',
      howWeHelp:
        'We work alongside you — keeping momentum, unblocking decisions and turning plans into completed actions.',
    },
    {
      id: 'weak-commercials',
      icon: 'calculator',
      title: 'Weak Commercial Foundations',
      description: 'Pricing, costs, delivery processes or sales targets have not been properly structured.',
      howWeHelp:
        'We structure pricing considerations, delivery processes and commercial targets so the business starts on workable foundations.',
    },
    {
      id: 'missing-systems',
      icon: 'boxes',
      title: 'Missing Systems',
      description: 'The business lacks a clear way to manage customers, work, information and performance.',
      howWeHelp:
        'We put practical systems in place from day one — customer tracking, workflows, reporting and administration that scale with you.',
    },
    {
      id: 'launch-execution',
      icon: 'rocket',
      title: 'Launch Execution',
      description: 'You know what should happen but need support to turn the plan into practical action.',
      howWeHelp:
        'We coordinate launch activity and stay hands-on through execution — enquiries, marketing and early operations working together.',
    },
  ],
};

export const LAUNCH_PROCESS = {
  eyebrow: 'Our Process',
  heading: 'From idea to reality',
  intro: 'Our structured process takes you from initial concept to a professionally launched and operational business.',
  stages: [
    {
      number: '01',
      id: 'explore',
      icon: 'search',
      title: 'Explore',
      text: 'We review the idea, objectives, intended customer, market opportunity and key assumptions.',
      includes: ['Idea review', 'Founder goals', 'Customer and market', 'Initial risks', 'Viability questions'],
    },
    {
      number: '02',
      id: 'plan',
      icon: 'clipboard-list',
      title: 'Plan',
      text: 'We build a practical roadmap covering the business model, priorities, costs, actions and launch requirements.',
      includes: ['Business model', 'Business plan', 'Pricing approach', 'Launch roadmap', 'Financial assumptions'],
    },
    {
      number: '03',
      id: 'build',
      icon: 'hammer',
      title: 'Build',
      text: 'We develop the foundations needed to present and operate the business professionally.',
      includes: ['Brand identity', 'Website', 'Customer journey', 'Operational processes', 'CRM and business systems'],
    },
    {
      number: '04',
      id: 'launch',
      icon: 'rocket',
      title: 'Launch',
      text: 'We coordinate the launch activity and help introduce the business to its intended market.',
      includes: ['Market launch plan', 'Marketing activity', 'Enquiry process', 'Sales foundations', 'Performance measures'],
    },
    {
      number: '05',
      id: 'grow',
      icon: 'trending-up',
      title: 'Grow',
      text: 'We remain available after launch to review performance, address gaps and support the next stage.',
      includes: ['Ongoing mentoring', 'Marketing support', 'Operational improvement', 'People support', 'Growth planning'],
    },
  ],
};

/**
 * Launch services & packages — data-driven and future-purchase-ready.
 *
 * DEVELOPER NOTE (future e-commerce):
 * - `slug` + `href` are the stable identifiers for future service-detail pages.
 * - `purchaseMode` supports: direct_purchase | pay_deposit | request_quote | book_consultation | contact_us
 * - `purchasable` and `depositOption` stay false, and `price` / `vat` stay null,
 *   until pricing, scope, terms and Stripe products are approved by Amden Group.
 * - `stripeProductId` / `stripePriceId` are reserved for the approved Stripe catalogue.
 * - `fullDescription`, `included`, `excluded`, `deliverables`, `format`,
 *   `duration` and `audience` stay empty until approved content is supplied.
 * - Full brand, website, systems and end-to-end launch programmes remain
 *   consultation-led unless later configured otherwise.
 */
export const LAUNCH_SERVICES = [
  {
    slug: 'business-idea-review',
    name: 'Business Idea Review',
    category: 'Validation',
    shortDescription:
      'A structured session to examine the concept, target customer, commercial assumptions, risks and practical next steps.',
    fullDescription: null,
    icon: 'lightbulb',
    featuredImage: null,
    audience: null,
    format: null,
    duration: null,
    included: [],
    excluded: [],
    deliverables: [],
    price: null,
    vat: null,
    depositOption: false,
    purchaseMode: 'direct_purchase',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/launch/business-idea-review',
    cta: 'details',
    availability: 'available',
    displayOrder: 1,
  },
  {
    slug: 'business-planning',
    name: 'Business Planning Support',
    category: 'Planning',
    shortDescription:
      'Practical support to build a usable business plan, commercial roadmap and set of launch priorities.',
    fullDescription: null,
    icon: 'clipboard-list',
    featuredImage: null,
    audience: null,
    format: null,
    duration: null,
    included: [],
    excluded: [],
    deliverables: [],
    price: null,
    vat: null,
    depositOption: false,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/launch/business-planning',
    cta: 'details',
    availability: 'available',
    displayOrder: 2,
  },
  {
    slug: 'launch-strategy',
    name: 'Launch Strategy',
    category: 'Strategy',
    shortDescription:
      'A structured strategy covering the offer, audience, positioning, route to market and launch actions.',
    fullDescription: null,
    icon: 'crosshair',
    featuredImage: null,
    audience: null,
    format: null,
    duration: null,
    included: [],
    excluded: [],
    deliverables: [],
    price: null,
    vat: null,
    depositOption: false,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/launch/launch-strategy',
    cta: 'details',
    availability: 'available',
    displayOrder: 3,
  },
  {
    slug: 'brand-starter',
    name: 'Brand Starter Package',
    category: 'Brand',
    shortDescription:
      'Core brand positioning, visual identity and messaging foundations for a professional launch.',
    fullDescription: null,
    icon: 'palette',
    featuredImage: null,
    audience: null,
    format: null,
    duration: null,
    included: [],
    excluded: [],
    deliverables: [],
    price: null,
    vat: null,
    depositOption: false,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/launch/brand-starter',
    cta: 'details',
    availability: 'available',
    displayOrder: 4,
  },
  {
    slug: 'business-website',
    name: 'Business Website Package',
    category: 'Website',
    shortDescription:
      'A professional website structured around credibility, customer journeys, enquiries and future growth.',
    fullDescription: null,
    icon: 'monitor',
    featuredImage: null,
    audience: null,
    format: null,
    duration: null,
    included: [],
    excluded: [],
    deliverables: [],
    price: null,
    vat: null,
    depositOption: false,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/launch/business-website',
    cta: 'details',
    availability: 'available',
    displayOrder: 5,
  },
  {
    slug: 'systems-starter',
    name: 'Systems Starter Package',
    category: 'Systems',
    shortDescription:
      'Practical CRM, workflow, reporting and administration foundations for a more organised launch.',
    fullDescription: null,
    icon: 'boxes',
    featuredImage: null,
    audience: null,
    format: null,
    duration: null,
    included: [],
    excluded: [],
    deliverables: [],
    price: null,
    vat: null,
    depositOption: false,
    purchaseMode: 'request_quote',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/launch/systems-starter',
    cta: 'details',
    availability: 'available',
    displayOrder: 6,
  },
  {
    slug: 'full-launch-programme',
    name: 'Full Business Launch Programme',
    category: 'Programme',
    shortDescription:
      'Joined-up support covering planning, structure, brand, website, systems and launch execution.',
    fullDescription: null,
    icon: 'rocket',
    featuredImage: null,
    audience: null,
    format: null,
    duration: null,
    included: [],
    excluded: [],
    deliverables: [],
    price: null,
    vat: null,
    depositOption: false,
    purchaseMode: 'book_consultation',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/launch/full-launch-programme',
    cta: 'discuss',
    availability: 'available',
    displayOrder: 7,
  },
  {
    slug: 'post-launch-support',
    name: 'Post-Launch Support',
    category: 'Support',
    shortDescription:
      'Ongoing practical support to review performance, solve early operational issues and build momentum.',
    fullDescription: null,
    icon: 'trending-up',
    featuredImage: null,
    audience: null,
    format: null,
    duration: null,
    included: [],
    excluded: [],
    deliverables: [],
    price: null,
    vat: null,
    depositOption: false,
    purchaseMode: 'contact_us',
    purchasable: false,
    stripeProductId: null,
    stripePriceId: null,
    href: '/launch/post-launch-support',
    cta: 'discuss',
    availability: 'available',
    displayOrder: 8,
  },
];

export const LAUNCH_SERVICES_SECTION = {
  eyebrow: 'Launch Services',
  heading: 'Choose the support you need',
  intro: 'Start with a focused service or work with Amden through the complete business-launch journey.',
};

export const LAUNCH_TOGETHER = {
  eyebrow: 'Joined-Up Support',
  heading: 'Everything brought together',
  copy: 'Launching a business requires more than a logo or business plan. Amden connects the strategy, brand, website, systems and practical implementation so each part supports the same commercial goal.',
  points: [
    'One structured launch roadmap',
    'Clear priorities and responsibilities',
    'Coordinated brand and website development',
    'Practical operating systems',
    'Consistent customer journey',
    'Support from planning through execution',
  ],
  consultation: {
    label: 'Start Here',
    heading: 'Book an initial launch consultation',
    copy: 'Tell us where you are in the journey and we’ll help identify the right next step.',
    button: 'Discuss Your Business',
    href: `${ENQUIRY_BASE}#solution-finder`,
  },
};

export const LAUNCH_WHY = {
  eyebrow: 'Why Build with Amden',
  heading: 'Your success becomes our business',
  copy: 'We do more than provide recommendations. We work alongside you to create the practical foundations, tools and actions required to move the business forward.',
  principles: [
    {
      icon: 'briefcase',
      title: 'Practical Experience',
      text: 'Commercial and operational thinking focused on what will work in the real world.',
    },
    {
      icon: 'layers',
      title: 'End-to-End Support',
      text: 'Planning, people, operations, brand, website and systems brought into one coordinated journey.',
    },
    {
      icon: 'hammer',
      title: 'Hands-On Delivery',
      text: 'Support that moves beyond advice and helps turn decisions into completed actions.',
    },
    {
      icon: 'trending-up',
      title: 'Built for Growth',
      text: 'Foundations designed to support the business beyond its initial launch.',
    },
  ],
  image: {
    src: 'https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&w=1080&q=80',
    alt: 'Amden consultant working alongside a founder on business plans',
    width: 1080,
    height: 810,
  },
};

export const LAUNCH_CASE_STUDY = {
  eyebrow: 'Case Study',
  heading: 'Contech DPD Business Launch',
  intro: 'A coordinated launch — brand, website and systems delivered together as one joined-up project.',
  challenge: {
    title: 'Challenge',
    text: 'Contech DPD was preparing to launch and needed a professional brand, website and core business systems in place before trading — without juggling multiple disconnected suppliers.',
  },
  solution: {
    title: 'Solution',
    text: 'Amden Launch provided joined-up support — creating the professional logo and brand identity, building and launching the new website, and configuring business email systems — all coordinated through one practical launch plan.',
  },
  results: {
    title: 'Results',
    points: [
      'Professional logo and brand identity created',
      'New website launched and fully supported',
      'Business email systems professionally configured',
    ],
  },
  cta: { label: 'Read Full Case Study', href: '/case-studies/contech-dpd' },
};

export const LAUNCH_CONSULTATION = {
  heading: 'Not sure where to begin?',
  copy: 'Tell us about your idea, what stage you are at and where you need support. We’ll help you identify the most practical next step.',
  button: 'Start the Conversation',
  baseHref: ENQUIRY_BASE,
};

export const LAUNCH_CTA = {
  index: '10',
  eyebrow: 'Next step',
  heading: 'Ready to start your business journey?',
  copy: 'Whether you are shaping an idea or preparing to launch, Amden can help you build the right foundations and move forward with clarity.',
  primaryLabel: 'Start Your Business Journey',
  primaryHref: `${ENQUIRY_BASE}#solution-finder`,
  secondaryLabel: 'Explore Launch Services',
  secondaryHref: '#launch-services',
};

// Session key used to preserve the visitor's chosen starting point
// between the selector and the enquiry journey.
export const LAUNCH_STARTING_POINT_KEY = 'amdenLaunchStartingPoint';
